import * as React from 'react';
import { cn } from '../../../lib/utils';

const TICK_DELAYS = [0, -0.7, -0.6, -0.5, -0.4, -0.3, -0.2, -0.1];
const TICK_COORDS: [string, string, string, string][] = [
    ['12', '2', '12', '6'],
    ['16.95', '7.05', '19.78', '4.22'],
    ['18', '12', '22', '12'],
    ['16.95', '16.95', '19.78', '19.78'],
    ['12', '18', '12', '22'],
    ['4.22', '19.78', '7.05', '16.95'],
    ['2', '12', '6', '12'],
    ['4.22', '4.22', '7.05', '7.05'],
];

interface HexLoadingToastProps {
    progress: number;
    isAdjacency: boolean;
    onClose: () => void;
}

export const HexLoadingToast = React.forwardRef<HTMLDivElement, HexLoadingToastProps>(
    ({ progress, isAdjacency }, ref) => {
        const isComplete = progress >= 100;

        return (
            <div
                ref={ref}
                className={cn(
                    'absolute top-5 right-5 z-40',
                    'flex items-center gap-2.5',
                    'rounded-[0.625rem] px-[0.875rem] py-2.5',
                    'whitespace-nowrap overflow-hidden',
                    'shadow-[0_2px_8px_rgba(0,0,0,0.12)]',
                    'animate-slide-in bg-white',
                )}
            >
                <div className="w-5 h-5 flex items-center justify-center shrink-0 text-yellow-800">
                    {!isComplete ? (
                        <svg className="w-[1.125rem] h-[1.125rem]" viewBox="0 0 24 24" fill="none">
                            {TICK_COORDS.map(([x1, y1, x2, y2], i) => (
                                <line
                                    key={i}
                                    x1={x1} y1={y1} x2={x2} y2={y2}
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    style={{
                                        animation: 'tickFade 0.8s linear infinite',
                                        animationDelay: `${TICK_DELAYS[i]}s`,
                                    }}
                                />
                            ))}
                        </svg>
                    ) : (
                        <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="12" fill="#16a34a" />
                            <path d="M7 12.5l3.5 3.5 6.5-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                </div>
                <span className="text-[0.8125rem] font-medium text-foreground leading-none">
                    {isComplete ? 'Successfully loaded Hexes' : isAdjacency ? 'Loading Adjacency' : 'Loading Hexes'}
                </span>
            </div>
        );
    }
);
HexLoadingToast.displayName = 'HexLoadingToast';

interface HexUnavailableToastProps {
    onClose: () => void;
    text?: string | null;
}

export const HexUnavailableToast = React.forwardRef<HTMLDivElement, HexUnavailableToastProps>(
    ({ onClose, text = null }, ref) => {
        React.useEffect(() => {
            const timer = setTimeout(onClose, 5000);
            return () => clearTimeout(timer);
        }, [onClose]);

        return (
            <div
                ref={ref}
                className={cn(
                    'absolute top-5 right-5 z-40',
                    'flex items-center gap-2.5',
                    'w-[17.5rem] h-[3.75rem]',
                    'rounded-[0.625rem] px-[0.875rem] py-2.5',
                    'overflow-hidden box-border',
                    'bg-white',
                    'shadow-[0_2px_8px_rgba(0,0,0,0.12)]',
                    'animate-slide-in',
                )}
            >
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 shrink-0">
                        <circle cx="12" cy="12" r="12" fill="rgba(161,98,7,1)" />
                        <text x="12" y="17" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold" fontFamily="serif">i</text>
                    </svg>
                </div>
                <span className="text-[0.8125rem] font-medium text-foreground leading-[1.3] flex-1 whitespace-normal">
                    {text}
                </span>
                <button
                    className="shrink-0 ml-1.5 flex items-center justify-center bg-transparent border-none p-0 cursor-pointer text-muted-foreground hover:text-foreground transition-colors"
                    onClick={onClose}
                    aria-label="Close"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>
        );
    }
);
HexUnavailableToast.displayName = 'HexUnavailableToast';
